import { Injectable } from '@angular/core';
import { NavigationBehaviorOptions, NavigationEnd, NavigationExtras, Params, QueryParamsHandling, Router, UrlTree } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, pairwise, startWith } from 'rxjs/operators';

import { CompanyContextServiceBase } from '@skylab-shared/core';
import { AppStates, NavigationRoute, State } from '@skylab-shared/scenes';

export const removeCompanyFragmentFromUrl = (url: string) => {
    if (!url) return url;
    return url.replace(/^\/?t\/\d+\/?/, "");
}

@Injectable({ providedIn: 'root' })
export class RoutingService {
    private _previousState: NavigationRoute | null;
    private _currentState: NavigationRoute;

    public currentStateParams$ = new Subject<{prev: Params, curr: Params}>();
    public states$ = new Subject<{prevUrl: string, currUrl: string}>();

    private inited = false;

    public constructor(
        private readonly router: Router
    ) {
        this.removeFbFragmentFromUrl();
        this.removeOldHashParamFragmentFromUrl();
    }

    public init(): void {
        if (this.inited) return;

        this.router.events
        .pipe(
            startWith(new NavigationEnd(null!, null!, null!)),
            filter((evt: any) => !evt || (evt instanceof NavigationEnd)),
            pairwise())
        .subscribe((elem: NavigationEnd[]) => {
            const [prev, curr] = elem;

            const prevBaseUrl = removeCompanyFragmentFromUrl(prev?.url?.split('?')[0].substring(1));
            const currBaseUrl = removeCompanyFragmentFromUrl(curr?.url?.split('?')[0].substring(1));

            if (prevBaseUrl === currBaseUrl) {
                const newParams = this.router.parseUrl(curr.url).queryParams ?? {};
                const oldParams = {...this.currentState.params};
                this._currentState = { url: currBaseUrl, params: newParams };

                this.currentStateParams$.next({curr: newParams, prev: oldParams});
            } else {
                this._previousState = prevBaseUrl != null ? { url: prevBaseUrl, params: this.router.parseUrl(prev.url).queryParams ?? {} } : null;
                this._currentState = { url: currBaseUrl, params: this.router.parseUrl(curr.url).queryParams ?? {} };
                this.states$.next({currUrl: currBaseUrl, prevUrl: prevBaseUrl});
            }
        });

        this.inited = true;
    }

    private removeOldHashParamFragmentFromUrl(): void {
        if (location.hash && location.hash.startsWith('#/')) {
            const newUrl = location.origin + location.pathname + location.hash.slice(2) + location.search;
            location.replace(newUrl);
        }
    }

    private removeFbFragmentFromUrl(): void {
        const fbFragment = '_=_';
        const url = location.toString();
        if (url.includes(fbFragment)) {
            location.replace(url.replace(fbFragment, ''));
        }
    }

    public open(url: string[], extras?: NavigationExtras, target = 'blank'): void {
        window.open(this.href(url, extras), target);
    }

    public href(url: string[], extras?: NavigationExtras): string {
        const urlTree = this.router.createUrlTree(url, extras);
        const serialized = this.router.serializeUrl(urlTree);
        return `${window.location.origin}/t/${CompanyContextServiceBase.companyContext.companyIdentityId}${serialized}`;
    }

    public get currentState(): NavigationRoute {
        return {...this._currentState };
    }

    public get previousState(): NavigationRoute | null {
        return this._previousState;
    }

    public async setQueryParamsAsync(queryParams: Params, mode: QueryParamsHandling = 'merge'): Promise<void> {
        await this.router.navigate([], { queryParams, queryParamsHandling: mode, replaceUrl: true });
    }

    public reloadCurrentState(): void {
        const current = { url: this.currentState.url, params: {...this.currentState.params}};
        this.navigateByUrl('dashboard', {skipLocationChange: true, })
            .then(() => this.navigate([current.url], { queryParams: current.params, skipLocationChange: true}))
            .then(() => {
                this._previousState = current;
            });
    }

    public get appStates(): State[] {
        return Object.keys(AppStates).map(key => AppStates[key as keyof typeof AppStates]);
    }

    public getExtrasFromPreviousState(): NavigationExtras | null {
        const current = this.router.currentNavigation();
        return current?.extras!;
    }

    public getStateFromUrl(url: string): { state: State, params: Params} | null {
        const urlObject = new URL(url);
        const stateFragment = removeCompanyFragmentFromUrl(urlObject.pathname ? urlObject.pathname.slice(1) : '');
        const paramsFragment = urlObject.search ? urlObject.search.slice(1) : '';

        if (!stateFragment) return null;

        const state = this.appStates.find(elem => elem.url === stateFragment);
        const params = paramsFragment?.split('&').reduce((prev, curr) => {
            const [key, value] = curr.split('=');
            prev[key] = value;
            return prev;
        }, {} as Params);

        return { state: state!, params };
    }

    //#region from base Router
    public navigate(commands: any[], extras?: NavigationExtras): Promise<boolean> {
        return this.router.navigate(['t', CompanyContextServiceBase.companyContext.companyIdentityId, ...commands[0].split('/')], extras);
    }

    public navigateByUrl(url: string | UrlTree, extras?: NavigationBehaviorOptions): Promise<boolean> {
        return this.router.navigateByUrl(`/t/${CompanyContextServiceBase.companyContext.companyIdentityId}/${url}`, extras);
    }
    //#endregion
}
